import type { GridApi } from "ag-grid-enterprise";
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import { ActiveGridContext } from "./ActiveGridContext";

/** Tracks the last-ready grid still alive. */
export function ActiveGridProvider({ children }: PropsWithChildren) {
  // Every mounted grid, oldest first. A page can show more than one — a modal
  // grid over the page's own — so the one that closes falls back to the one
  // underneath instead of leaving the app with no grid at all.
  const mounted = useRef<GridApi[]>([]);
  const [api, setApi] = useState<GridApi | null>(null);

  const register = useCallback((next: GridApi) => {
    mounted.current = [...mounted.current, next];
    setApi(next);
  }, []);

  const unregister = useCallback((gone: GridApi) => {
    mounted.current = mounted.current.filter((it) => it !== gone);
    setApi((current) =>
      current === gone ? (mounted.current.at(-1) ?? null) : current
    );
  }, []);

  const value = useMemo(
    () => ({ api, register, unregister }),
    [api, register, unregister]
  );

  return (
    <ActiveGridContext.Provider value={value}>
      {children}
    </ActiveGridContext.Provider>
  );
}
